import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span>{article.viewCount.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-5">
          <span className="inline-block px-2 py-0.5 rounded text-xs font-sans bg-accent-100 text-accent-800 mb-3">
            {article.tags[0]}
          </span>
          <h3 className="font-heading text-lg font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-foreground-400">{article.publishDate}</span>
          <span className="inline-block px-2 py-0.5 rounded text-xs font-sans bg-secondary-100 text-secondary-700">
            {article.tags[0]}
          </span>
        </div>
        <h3 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mt-1.5">
          {article.excerpt}
        </p>
      </div>
    </Link>
  );
}